import type { Ref } from 'react';
import { forwardRef } from 'react';
import CharacterCount from './CharacterCount';
import type { InputBaseProps, InputRenderProps } from './InputBase';
import InputBase from './InputBase';

export type TextAreaProps = Omit<InputBaseProps, 'element' | 'type' | 'children'> & {
  /**
   * If the char count should be displayed.
   */
  showCharCount?: boolean;
  /**
   * If we should select all of the text when the textarea is focused.
   * This is useful for cases where the user is expected to copy the text.
   */
  shouldSelectTextOnFocus?: boolean;
};

/**
 * A generic, controlled textarea for FOSSA Form elements.
 */
const TextArea = ({
  label,
  value,
  name,
  onChange,
  size,
  placeholder,
  isRequired,
  isDisabled,
  onKeyDown,
  testId,
  maxLength,
  showCharCount,
  isResizable,
  shouldSelectTextOnFocus,
}: TextAreaProps, ref: Ref<HTMLTextAreaElement>) => (
  <InputBase
    element="textarea"
    label={label}
    placeholder={placeholder}
    value={value}
    size={size}
    onChange={onChange}
    isRequired={isRequired}
    isDisabled={isDisabled}
    onKeyDown={onKeyDown}
    testId={testId}
    maxLength={maxLength}
    isResizable={isResizable}
    name={name}
  >
    {(textareaProps: InputRenderProps) => (maxLength && showCharCount) ? (
      <>
        <textarea
          ref={ref}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...textareaProps}
          onFocus={shouldSelectTextOnFocus ? (ev) => ev.currentTarget.select() : undefined}
        />
        <CharacterCount
          maxLength={maxLength}
          currentLength={value.length}
        />
      </>
    ) : (
      <textarea
        ref={ref}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...textareaProps}
        onFocus={shouldSelectTextOnFocus ? (ev) => ev.currentTarget.select() : undefined}
      />
    )}
  </InputBase>
);

export default forwardRef<HTMLTextAreaElement, TextAreaProps>(TextArea);
